import { useEffect, useRef, useState } from 'react';

/** Hash router: #/c/<campaign>/s/<session> etc. */
export function useRoute(): string[] {
  const parse = () => window.location.hash.replace(/^#\/?/, '').split('?')[0].split('/').filter(Boolean).map(decodeURIComponent);
  const [parts, setParts] = useState(parse);
  useEffect(() => {
    const on = () => setParts(parse());
    window.addEventListener('hashchange', on);
    return () => window.removeEventListener('hashchange', on);
  }, []);
  return parts;
}

export function hashParam(name: string): string | null {
  const q = window.location.hash.split('?')[1];
  return q ? new URLSearchParams(q).get(name) : null;
}

export function go(path: string) {
  window.location.hash = path.startsWith('#') ? path : `#${path}`;
}

const dtf = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' });
const dfull = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' });
export const fmtTime = (iso: string) => dtf.format(new Date(iso));
export const fmtDateTime = (iso: string) => dfull.format(new Date(iso));

export function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/** Server-sent events: calls fn when the server reports a change (filtered by campaign). */
export function useServerEvents(campaignId: string | null, fn: (what: string, data: any) => void) {
  const ref = useRef(fn);
  ref.current = fn;
  useEffect(() => {
    let es: EventSource | null = null;
    let closed = false;
    const open = () => {
      if (closed) return;
      es = new EventSource('/api/events');
      es.onmessage = (m) => {
        try {
          const d = JSON.parse(m.data);
          if (!campaignId || !d.campaignId || d.campaignId === campaignId) ref.current(d.what, d);
        } catch {
          /* ignore */
        }
      };
    };
    open();
    return () => {
      closed = true;
      es?.close();
    };
  }, [campaignId]);
}

export const TYPE_LABEL: Record<string, string> = {
  pc: 'PC',
  npc: 'NPC',
  location: 'Location',
  faction: 'Faction',
  item: 'Item',
  quest: 'Quest',
  clue: 'Clue',
  question: 'Question',
  other: 'Other',
};

export const KIND_LABEL: Record<string, string> = {
  fact: 'Confirmed fact',
  statement: 'Someone said',
  rumor: 'Rumor',
  theory: 'Theory',
  belief: 'Character belief',
};

export function classNames(...c: (string | false | null | undefined)[]) {
  return c.filter(Boolean).join(' ');
}

/** Renders FTS snippet markers (\u0002 … \u0003) as <mark> safely (no HTML injection). */
export function splitMarks(s: string): { text: string; mark: boolean }[] {
  const out: { text: string; mark: boolean }[] = [];
  let mark = false;
  let buf = '';
  for (const ch of s) {
    if (ch === '\u0002' || ch === '\u0003') {
      if (buf) out.push({ text: buf, mark });
      buf = '';
      mark = ch === '\u0002';
    } else buf += ch;
  }
  if (buf) out.push({ text: buf, mark });
  return out;
}
